import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ChartOfAccountLedgerDialog } from "@/components/dataBank/ChartOfAccountLedgerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { can, getStoredAuthUser } from "@/lib/authUser";
import {
  collectLeafAccountIds,
  fetchChartOfAccountsTree,
  filterChartTree,
  flattenChartTree,
  uniqueAccountClasses,
  type ChartOfAccountNode,
} from "@/lib/dataBank/chartOfAccountsStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { BookOpen, ChevronDown, ChevronRight, Loader2, RotateCcw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

function accountNameClass(level: 1 | 2 | 3): string {
  if (level === 1) {
    return "font-bold text-teal-700 dark:text-teal-300";
  }
  if (level === 2) {
    return "font-semibold text-sky-700 dark:text-sky-300";
  }
  return "font-medium text-slate-700 dark:text-slate-200";
}

function formatBalance(amount: number, side: "Dr" | "Cr", symbol: string): string {
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const prefix = symbol ? `${symbol} ` : "";
  return `${prefix}${formatted} ${side}.`;
}

const ChartOfAccounts = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState<ChartOfAccountNode[]>([]);
  const [query, setQuery] = useState("");
  const [accountClass, setAccountClass] = useState("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [ledgerNode, setLedgerNode] = useState<ChartOfAccountNode | null>(null);

  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "$";

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchChartOfAccountsTree();
      setTree(data);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[ChartOfAccounts] Load failed", error);
      toast.error("Could not load chart of accounts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const classes = useMemo(() => uniqueAccountClasses(tree), [tree]);
  const filtered = useMemo(() => filterChartTree(tree, query, accountClass), [tree, query, accountClass]);
  const rows = useMemo(() => flattenChartTree(filtered), [filtered]);
  const visibleRows = useMemo(() => {
    const hidden = new Set<string>();
    const isHidden = (node: ChartOfAccountNode, parentCollapsed: boolean): boolean => {
      if (parentCollapsed) hidden.add(node.id);
      const selfCollapsed = collapsed.has(node.id) || parentCollapsed;
      for (const child of node.children) {
        isHidden(child, selfCollapsed);
      }
      return selfCollapsed;
    };
    for (const node of filtered) isHidden(node, false);
    return rows.filter((row) => !hidden.has(row.id));
  }, [filtered, rows, collapsed]);

  const toggle = (id: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ledgerAccountIds = useMemo(
    () => (ledgerNode ? collectLeafAccountIds(ledgerNode) : []),
    [ledgerNode],
  );

  const resetFilters = () => {
    setQuery("");
    setAccountClass("all");
  };

  const openLedger = (row: ChartOfAccountNode) => {
    if (collectLeafAccountIds(row).length === 0) return;
    setLedgerNode(row);
  };

  if (!canView) {
    return (
      <PermissionDenied
        title="Chart of accounts access required"
        message="You don’t have permission to view chart of accounts. Ask an administrator to grant chart_of_account_can_view_any."
      />
    );
  }

  return isForbidden ? (
    <PermissionDenied
      title="Chart of accounts access required"
      message="You don’t have permission to view chart of accounts. Ask an administrator to grant chart_of_account_can_view_any."
    />
  ) : (
    <Layout title="Chart of Accounts">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Chart of Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Level 1 account types, level 2 groups, and level 3 accounts with balances.{" "}
            <Link to="/dashboard" className="text-primary hover:underline font-medium">
              Home
            </Link>
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="glass-input h-10 pl-9"
              placeholder="Search account"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Select value={accountClass} onValueChange={setAccountClass}>
            <SelectTrigger className="h-10 w-full sm:w-56">
              <SelectValue placeholder="Select account class" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {classes.map((cls) => (
                <SelectItem key={cls} value={cls}>
                  {cls}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="glass" className="h-10 gap-2" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center rounded-xl border border-border/50 bg-card/20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-border/50 bg-card/20 p-6 text-center">
            <BookOpen className="mb-3 h-10 w-10 text-muted-foreground/50" />
            <h3 className="font-semibold text-foreground">No accounts found</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {query || accountClass !== "all"
                ? "No accounts match your filters."
                : "No chart of accounts have been created for this company yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Account Code</TableHead>
                  <TableHead>Account Class</TableHead>
                  <TableHead>Account Group</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((row) => {
                  const indent = row.level === 1 ? 0 : row.level === 2 ? 16 : 36;
                  const hasChildren = row.children.length > 0;
                  const isOpen = !collapsed.has(row.id);
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: indent }}>
                          {hasChildren ? (
                            <button
                              type="button"
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                              onClick={() => toggle(row.id)}
                              aria-label={isOpen ? "Collapse" : "Expand"}
                            >
                              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          ) : (
                            <span className="inline-block w-6 shrink-0" />
                          )}
                          <span className={cn("truncate rounded-md px-2 py-0.5", accountNameClass(row.level))}>
                            {row.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{row.code || "—"}</TableCell>
                      <TableCell>{row.accountClass}</TableCell>
                      <TableCell>{row.level === 3 ? row.accountGroup || "N/A" : "N/A"}</TableCell>
                      <TableCell
                        className={cn(
                          "text-right tabular-nums",
                          row.level === 3 ? "font-medium text-foreground" : "text-muted-foreground",
                        )}
                      >
                        <button
                          type="button"
                          className="inline-flex min-h-8 items-center justify-end rounded-md px-1.5 py-0.5 text-primary underline-offset-4 hover:bg-primary/10 hover:underline"
                          onClick={() => openLedger(row)}
                        >
                          {formatBalance(row.balance, row.balanceSide, currencySymbol)}
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <ChartOfAccountLedgerDialog
          open={ledgerNode != null}
          node={ledgerNode}
          accountIds={ledgerAccountIds}
          currencySymbol={currencySymbol}
          onOpenChange={(open) => {
            if (!open) setLedgerNode(null);
          }}
        />
      </div>
    </Layout>
  );
};

export default ChartOfAccounts;
